import { ChangeDetectorRef, Component, Input, OnChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ParticipantMarketType, ParticipantOption, ParticipantPriceType, ParticipantType } from '@cds/betting-offer';
import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import {
    FixtureParticipantPriceMap,
    HorseRaceSortingDirection,
    TimeFormParticipantBase,
    TimeformParticipantWithEventOption,
    TimeformParticipantWithParticipantOption,
    WinGameFixture,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';

import { ParticipantPickType } from '../../../../betslip/core/picks/pick-models';
import { CrmPromotionProviderService, PromotionMap } from '../../../crm-promotion/crm-promotion-provider.service';
import { DropdownOption, OptionType } from '../../../dropdown/models';
import { HorsesFixtureHelper } from '../../../horse-racing-shared/services/horses-fixture-helper.service';
import { HorsesFixtureMarketHelper } from '../../../horse-racing-shared/services/horses-fixture-market-helper.service';
import { HorsesFixtureParticipantHelper } from '../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from '../../../horse-racing-shared/services/horses-fixture-sorting-helper.service';
import { GreyhoundTrackingService } from '../../common/greyhound-tracking.service';
import { HorsesRaceService } from '../../services/horses-race.service';
import { BaseRaceWinMarketService } from '../base/base-race-win-market.service';

export interface HorsesRaceWinMarketComponentViewModel {
    sortBy: HorseRaceSortingDirection;
    timeformSpotlightIndexes: number[];
    timeformTopRunner?: WinGameParticipant;
    timeformSpotlightDisplay: boolean;
    expandedParticipant: { [id: number]: boolean };
    runners: (TimeformParticipantWithEventOption | TimeformParticipantWithParticipantOption)[];
    nonRunners: string;
    allPricesLocked: boolean;
    allPricesSP: boolean;
    hasSilks: boolean; // if there is at least one silk so to show silks for all in order to keep indentation
}

@Component({
    selector: 'ms-horses-race-win-market',
    templateUrl: 'horses-race-win-market.component.html',
    standalone: false,
})
export class HorsesRaceWinMarketComponent implements OnChanges {
    @Input() displaySorting = true;
    @Input() participantOptionGroups: { [id: string]: EventOption[] };
    @Input() optionGroup: EventOptionGroup;
    @Input() displayType: OptionGroupType;
    @Input() optionPickTrackingsource: string;
    @Input() trackingModuleType: string;
    @Input() trackingOptions: { [key: string]: string };
    @Input() selectedMarketTitle: string;

    @Input()
    set fixture(fixture: WinGameFixture) {
        const isNewFixture = !this._fixture || this._fixture.id !== fixture.id;
        this._fixture = fixture;
        this.setViewModel(isNewFixture);
    }

    get fixture(): WinGameFixture {
        return this._fixture!;
    }

    private _fixture: WinGameFixture | undefined;
    vm: HorsesRaceWinMarketComponentViewModel = {
        sortBy: HorseRaceSortingDirection.Odds,
        timeformSpotlightIndexes: [],
        timeformSpotlightDisplay: false,
        expandedParticipant: {},
        runners: [],
        nonRunners: '',
        allPricesLocked: false,
        allPricesSP: false,
        hasSilks: false,
    };

    priceType = {
        Fixed: ParticipantPriceType.Fixed,
        StartingPrice: ParticipantPriceType.StartingPrice,
    };

    promotedOptionIdsMap: StringDictionary<number> = {};
    activeOption: DropdownOption;
    dropDownItems: DropdownOption[];
    isAllexpanded: boolean;
    isOptionBet: boolean;
    winMarketSuspended: boolean;
    groupTypes = OptionGroupType;
    pickType: ParticipantPickType;

    constructor(
        public sitecore: Sitecore,
        protected horsesFixtureSortingHelper: HorsesFixtureSortingHelper,
        protected horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected horsesFixtureMarketHelper: HorsesFixtureMarketHelper,
        protected horsesRaceService: HorsesRaceService,
        protected crmPromotionProvider: CrmPromotionProviderService,
        protected changeDetector: ChangeDetectorRef,
        protected horsesFixtureHelper: HorsesFixtureHelper,
        protected greyhoundTrackingService: GreyhoundTrackingService,
        protected baseRaceWinMarketService: BaseRaceWinMarketService,
    ) {
        this.crmPromotionProvider.promotionsMap$.pipe(takeUntilDestroyed()).subscribe((ids) => this.promotionMapChanged(ids));
        this.dropDownItems = [
            {
                id: HorseRaceSortingDirection.Odds,
                name: this.sitecore.horseRacing.raceDetails.OddsSorting,
                type: OptionType.Regular,
            },
            {
                id: HorseRaceSortingDirection.CardNumber,
                name: this.sitecore.horseRacing.raceDetails.CardNumberSorting,
                type: OptionType.Regular,
            },
        ];
    }

    ngOnChanges(changes: ISimpleChanges<HorsesRaceWinMarketComponent>): void {
        const isselectedMarketTitleChanged =
            changes.selectedMarketTitle && changes.selectedMarketTitle?.currentValue !== changes.selectedMarketTitle?.previousValue;
        this.setViewModel(!!isselectedMarketTitleChanged);
        this.pickType = this.displayType === this.groupTypes.Wincast ? ParticipantPickType.Win : ParticipantPickType.Derived;
    }

    /**
     * Set the view model.
     *
     * @param isNewFixture defines if this is initial loading of a fixture. The we use default sorting
     * if is updated fixture we do not re-sort items because we want to keep participant positions, so in case of odd changes the participant will not
     * change its position as it is been loaded.
     */
    setViewModel(isNewFixture: boolean): void {
        this.isOptionBet = this.fixture.isOptionBet;
        const runners = this.horsesFixtureParticipantHelper.getParticipants(this.fixture.participants);
        const options = this.horsesFixturePricingHelper.getPrices(this.fixture, ParticipantMarketType.Win, ParticipantPriceType.Fixed);
        this.vm.allPricesLocked = this.horsesFixturePricingHelper.allPricesAreLocked(this.fixture, runners, options);
        this.vm.allPricesSP = this.horsesFixturePricingHelper.allPricesAreSP(options, runners);
        this.winMarketSuspended = this.horsesFixtureMarketHelper.isWinMarketFixedOddsLocked(this.fixture);
        let participants;
        if (isNewFixture) {
            // use default sorting
            this.vm.sortBy = this.baseRaceWinMarketService.getSortDirection(this.fixture, this.vm.allPricesLocked, this.vm.allPricesSP);
            participants = this.horsesFixtureSortingHelper.sortParticipantsBy(runners, options, this.vm.sortBy);
            this.isAllexpanded = false;
            this.vm.expandedParticipant = {};
            this.activeOption = this.baseRaceWinMarketService.getActiveSortOption(this.vm.sortBy);
        } else {
            // restore sorting as it was before.
            const sorted = this.horsesRaceService.restoreParticipantOrder(this.vm.runners, this.fixture);
            participants = this.horsesFixtureParticipantHelper.getParticipants(sorted);
        }
        this.vm.runners = this.buildRunners(participants, options);
        this.vm.nonRunners = this.horsesFixtureParticipantHelper.getCombinedNonRunners(this.fixture);
        this.vm.hasSilks = this.vm.runners.some((_) => !!_.attributes.silkUrl);
        if (this.activeOption.id === HorseRaceSortingDirection.Odds) this.sortParticipantsBy(HorseRaceSortingDirection.Odds);
    }

    orderBy(sortBy: string | number): void {
        const sortParsed = Object.values(HorseRaceSortingDirection).find((value) => value.toString() === sortBy);

        if (sortParsed) {
            this.activeOption.id = sortParsed;
            this.sortParticipantsBy(sortParsed);
            this.greyhoundTrackingService.trackSorting(this.fixture, sortParsed);
        }
    }

    sortParticipantsBy(sortBy: HorseRaceSortingDirection): void {
        this.vm.sortBy = sortBy;
        const { horses, options } = this.vm.runners.reduce(
            (aggr, cur) => {
                aggr.horses.push(cur);
                aggr.options[cur.id] = cur.option as ParticipantOption;

                return aggr;
            },
            { horses: [] as WinGameParticipant[], options: {} as FixtureParticipantPriceMap },
        );
        const sortedRunners = this.horsesFixtureSortingHelper.sortParticipantsBy(horses, options, sortBy);
        const participants = this.horsesFixtureParticipantHelper.getParticipants(sortedRunners);
        this.vm.runners = this.buildRunners(participants, options);
    }

    toggleRunnerInfo(participant: WinGameParticipant): void {
        if (participant.properties!.type !== ParticipantType.Favourite) {
            const noFavouriteParticipants = this.vm.runners.filter((p) => p.properties?.type !== ParticipantType.Favourite);
            this.vm.expandedParticipant[participant.id] = !this.baseRaceWinMarketService.getExpandedState(participant, this.vm.expandedParticipant);
            if (noFavouriteParticipants.every((t) => this.vm.expandedParticipant[t.id])) {
                this.isAllexpanded = true;
            } else if (noFavouriteParticipants.every((t) => !this.vm.expandedParticipant[t.id])) {
                this.isAllexpanded = false;
            }
        }

        this.greyhoundTrackingService.trackExpandCollapse(this.vm.expandedParticipant[participant.id], this.fixture, participant);
    }

    trackParticipant(index: number, runner: TimeFormParticipantBase): number {
        return runner.id;
    }

    private promotionMapChanged(promoMap: PromotionMap): void {
        this.promotedOptionIdsMap = promoMap.options;
        this.changeDetector.markForCheck();
    }

    protected buildRunners(
        participants: WinGameParticipant[],
        options: FixtureParticipantPriceMap,
    ): (TimeformParticipantWithEventOption | TimeformParticipantWithParticipantOption)[] {
        return participants.map((p) => {
            const option = options[p.id];
            return {
                ...p,
                attributes: this.horsesFixtureParticipantHelper.getHorseRaceInfo(p),
                option,
                priceHistory: this.horsesFixturePricingHelper.getPriceHistory(option),
                isOptionActive: this.horsesFixturePricingHelper.isAvailableWinOption(option),
            };
        });
    }

    toggleExpandCollapse(): void {
        const noFavouriteParticipants = this.vm.runners.filter((p) => p.properties?.type !== ParticipantType.Favourite);
        if (!this.isAllexpanded) {
            noFavouriteParticipants.forEach((participant) => {
                this.vm.expandedParticipant[participant.id] = true;
            });
            this.isAllexpanded = true;
        } else {
            noFavouriteParticipants.forEach((participant) => {
                this.vm.expandedParticipant[participant.id] = false;
            });
            this.isAllexpanded = false;
        }
        this.greyhoundTrackingService.trackExpandCollapse(this.isAllexpanded, this.fixture, null);
    }

    trackDropdownToggle(event: any): void {
        this.greyhoundTrackingService.trackSorting(this.fixture);
    }

    isEventOption(option: EventOption | ParticipantOption | undefined): option is EventOption {
        return option instanceof EventOption;
    }

    isParticipantOption(option: EventOption | ParticipantOption | undefined): option is ParticipantOption {
        return !!option && 'fixtureParticipantId' in option;
    }
}
